import { useState } from 'react';
import { Loader2, Trash2 } from 'lucide-react';

import { Button } from '@/shared/components/ui/button';
import { Sheet } from '@/shared/components/ui/sheet';
import { messages } from '@/shared/constants/messages';
import { toUserMessage } from '@/shared/lib/errors';

interface DiscardDraftButtonProps {
  isDiscarding: boolean;
  error: unknown;
  onConfirm: () => void;
}

/**
 * Descartar o rascunho — o botão e a pergunta.
 *
 * Duas coisas mudaram, e as duas são a mesma ideia.
 *
 * **O botão fica quieto.** Ele é discreto em repouso (cinza, texto pequeno,
 * fora da coluna de leitura) e só assume o vermelho no `hover`/foco, quando o
 * dedo já está sobre ele. Uma ação destrutiva sinalizada o tempo todo ao lado
 * de um formulário de rascunho compete com o que a pessoa veio fazer — e cor
 * de perigo que fica ligada para de ser lida como perigo.
 *
 * **A pergunta é uma janela, não um `window.confirm`.** O diálogo nativo do
 * navegador vem sem o idioma do painel, com "OK/Cancelar" em vez dos verbos da
 * ação, travando a aba inteira e com a cara do Chrome — no meio de um sistema
 * que 150 voluntários precisam reconhecer como confiável, ele lê como golpe.
 * Aqui os dois botões dizem o que fazem, e o que NÃO destrói é o que ganha o
 * foco inicial.
 */
export function DiscardDraftButton({ isDiscarding, error, onConfirm }: DiscardDraftButtonProps) {
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  return (
    <>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => setIsConfirmOpen(true)}
        className="text-muted-foreground hover:bg-destructive/10 hover:text-destructive focus-visible:text-destructive"
      >
        <Trash2 aria-hidden="true" className="size-4" />
        {messages.form.discard}
      </Button>

      <Sheet
        open={isConfirmOpen}
        onClose={() => setIsConfirmOpen(false)}
        placement="center"
        dismissible={!isDiscarding}
        title={messages.form.discardConfirmTitle}
        description={messages.form.discardConfirm}
        footer={
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            {/* Primeiro no DOM: é ele que recebe o foco ao abrir, e `Enter` no
                reflexo de quem estava digitando mantém o rascunho. */}
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsConfirmOpen(false)}
              disabled={isDiscarding}
            >
              {messages.form.discardKeep}
            </Button>
            <Button type="button" variant="destructive" onClick={onConfirm} disabled={isDiscarding}>
              {isDiscarding ? (
                <Loader2 aria-hidden="true" className="size-4 animate-spin" />
              ) : (
                <Trash2 aria-hidden="true" className="size-4" />
              )}
              {isDiscarding ? messages.form.discarding : messages.form.discardConfirmAction}
            </Button>
          </div>
        }
      >
        {error ? (
          <p
            role="alert"
            className="animate-reveal rounded-lg border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive"
          >
            {toUserMessage(error)}
          </p>
        ) : null}
      </Sheet>
    </>
  );
}
